import React, { useEffect, useMemo, useState } from "react"

import { type Theme, css } from "@emotion/react"
import type { Interpolation } from "@emotion/serialize"

import {
    BrowserStorageType,
    FRONT_GENERAL_CONFIG,
} from "../config/general.config"
import { NIRVANA_COLORS, NIRVANA_THEME } from "../config/theme.config"
import { BrowserStorageHelper } from "../helpers/browserStorage.helper"
import { PrimaryText } from "./primaryText.element"

import closeIcon from "@front-shared/assets/images/icons/close-dark.svg"
import expandIcon from "@front-shared/assets/images/icons/expand-dark.svg"

export const PromoPopupWrapperElement: React.FunctionComponent<
    IPromoPopupWrapperProps
> = props => {
    const [isCollapsed, setIsCollapsed] = useState<boolean>(true)

    const [isEnabled, setIsEnabled] = useState<boolean>(false)

    const storagePromoClosedKey = useMemo<string>(
        () =>
            `${FRONT_GENERAL_CONFIG.browserStorageKeys.promoClosedAt}-${props.name}`,
        [props.name],
    )

    // Set isEnabled with timeout so it flows nicely from bottom using transition of height
    // Also set isCollapsed based on browserStorage value - if was closed once, don't show for another 15 minutes
    useEffect(() => {
        const closedAt: null | number | string = BrowserStorageHelper.get(
            storagePromoClosedKey,
            null,
            BrowserStorageType.sessionStorage,
        )

        const shouldBeCollapsed =
            !!props.isDefaultCollapsed ||
            (closedAt && +closedAt + 15 * 60 * 1000 > new Date().getTime())

        setIsCollapsed(!!shouldBeCollapsed)

        const timeoutId = setTimeout(() => {
            setIsEnabled(true)
        }, 1000)

        return () => {
            try {
                clearTimeout(timeoutId)
            } catch (e) {}
        }
    }, [])

    const togglePopup = (recordClosed?: boolean) => {
        setIsCollapsed(current => !current)

        if (recordClosed) {
            BrowserStorageHelper.set(
                storagePromoClosedKey,
                new Date().getTime(),
                BrowserStorageType.sessionStorage,
            )
        }
    }

    return (
        <div
            className={`
                relative
                ${props.className}
            `}
        >
            <div
                css={[
                    styles.popup,
                    isCollapsed ? styles.popupCollapsed : undefined,

                    css({
                        width: props.width || 530,
                    }),
                    styles.popupDefaultMobileWidth,

                    isEnabled
                        ? [
                              css({
                                  height: !isCollapsed
                                      ? props.height || 235
                                      : props.collapsedHeight || 60,
                              }),
                          ]
                        : css({ height: 0 }),
                    props.popupCss,
                ]}
                className={props.popupClassName}
                onClick={
                    isCollapsed
                        ? () => {
                              togglePopup()
                          }
                        : undefined
                }
            >
                {/*TITLE*/}
                <div
                    className={isCollapsed ? "flex h-full" : ""}
                    css={[styles.titleWrapper]}
                >
                    {isCollapsed ? (
                        <div className="flex items-center w-full">
                            <PrimaryText
                                typography="h5"
                                className="flex-1"
                                css={[styles.collapsedTitle]}
                            >
                                {props.collapsedTitle}
                            </PrimaryText>

                            <div css={styles.expandIcon}>
                                <img
                                    src={expandIcon}
                                    alt="Expand"
                                    title="Expand"
                                />
                            </div>
                        </div>
                    ) : (
                        <div
                            css={styles.closeIcon}
                            onClick={() => {
                                togglePopup(true)
                            }}
                        >
                            <img src={closeIcon} alt="Close" title="Close" />
                        </div>
                    )}
                </div>

                {/*CONTENT*/}
                {!isCollapsed && (
                    <div
                        css={[styles.contentInner]}
                        className={props.contentClassName}
                    >
                        {props.content}
                    </div>
                )}
            </div>
        </div>
    )
}

const styles = {
    popup: css({
        position: "fixed",
        right: 50,
        bottom: 0,
        zIndex: 45,
        backgroundColor: NIRVANA_COLORS.brand.offWhite,
        border: `1px solid ${NIRVANA_COLORS.brand.warmShadow}`,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        paddingLeft: 32,
        paddingRight: 32,
        transitionDuration: "1s",
        transitionProperty: "height",
    }),
    popupCollapsed: css({
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        cursor: "pointer",
        paddingRight: 25,
    }),
    collapsedTitle: css({
        position: "relative",
        top: 3,
    }),
    popupDefaultMobileWidth: css({
        [NIRVANA_THEME.mediaQueries.md]: {
            right: 0,
            width: "100vw",
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
        },
    }),
    titleWrapper: css({
        minHeight: 40,
    }),
    expandIcon: css({
        marginLeft: 15,
    }),
    closeIcon: css({
        position: "absolute",
        right: 15,
        top: 15,
        zIndex: 51,
        padding: 10,
        cursor: "pointer",
    }),
    contentInner: css({
        overflowY: "auto",
        paddingBottom: 16,
    }),
}

interface IPromoPopupWrapperProps {
    className?: string
    name: string
    popupCss?: Interpolation<Theme>
    popupClassName?: string
    height?: number
    collapsedHeight?: number
    width?: number
    isDefaultCollapsed?: boolean
    collapsedTitle: string
    content: React.JSX.Element
    contentClassName?: string
}
